import React from 'react';
import TableHeader from './TableHeader.tsx';
import { Link } from 'react-router-dom';
import "../../../styles/admin/Modal.css"
import { formatCurrency, formatNumbers } from '../../../helpers/functions.tsx';
import {
    getAdminFiguresDateKey as getFiguresDateKey,
    getAdminFiguresWeekdayIndex as getFiguresWeekdayIndex,
} from '../../../helpers/adminFiguresTime.tsx';
import { logUserActivity } from '../../../services/player/LoginService.tsx';
import { useTableColumns } from './useTableColumns.tsx';
import { buildVisitedUserNavState } from '../../../helpers/adminVisitedUserCache.tsx';
import { WeeklyFiguresDayTotals, WeeklyFiguresDetailIds } from '../../../services/admin/BetsService.tsx';

type WeeklyFiguresProps = {
    customer: any;
    weekDays: Date[];
    formatDay: (date: any) => string;
    filterPlayers: (players: any) => any[];
    filters: any;
    totalPendingPlayer: Record<string, number>;
    totalPendingsAmount: number;
    dailyTotals: Record<string, WeeklyFiguresDayTotals>;
    detailIdsByPlayer: Record<string, WeeklyFiguresDetailIds>;
    currentUser: any;
    currentColumnIndex: number;
    setCurrentColumnIndex: (n: number) => void;
    userIpDataAdmin: any;
    weeklyDepositsWithdrawalsByPlayer: Record<string, number>;
    onDayClick: (playerId: string, targetDate: Date) => void | Promise<void>;
    onWeekClick: (playerId: string, targetDate: Date) => void | Promise<void>;
    onDepositsWithdrawalsClick: (playerId: string) => void | Promise<void>;
    onPendingClick: (playerId: string) => void | Promise<void>;
};

const WeeklyFigures = ({
    customer, weekDays, formatDay, filterPlayers, filters, totalPendingPlayer,
    totalPendingsAmount, dailyTotals, detailIdsByPlayer, currentUser,
    currentColumnIndex, setCurrentColumnIndex, userIpDataAdmin,
    weeklyDepositsWithdrawalsByPlayer,
    onDayClick, onWeekClick, onDepositsWithdrawalsClick, onPendingClick,
}: WeeklyFiguresProps) => {

    const filterPlayersByAgent = (agentName: any) => filterPlayers(customer.players.filter((player: any) => player.parent === agentName));
    const filteredPlayers = filterPlayers(customer.players);
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const days1 = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

    const { isVisible } = useTableColumns(filters, currentColumnIndex);

    const renderPlayerRow = (player: any) => {
        const playerWeeklyTotal = dailyTotals[player.id]?.weeklyTotal || 0;
        const dayMap = dailyTotals[player.id];
        const ids = detailIdsByPlayer[player.id];

        const hasWeekData = Boolean(
            ids && (
                ids.allBetIds.length > 0 ||
                ids.allAdjustmentIds.length > 0 ||
                ids.allCasinoIds.length > 0 ||
                playerWeeklyTotal !== 0
            )
        );

        const depositsTotal = weeklyDepositsWithdrawalsByPlayer[player.id] || 0;
        const hasDepositsWithdrawals = (ids?.depositWithdrawalIds.length ?? 0) > 0 || depositsTotal !== 0;
        const pendingAmount = totalPendingPlayer[player.id] || 0;
        const hasPending = (ids?.pendingBetIds?.length ?? 0) > 0 || pendingAmount !== 0;

        return (
            <tr key={player.id}>
                <td className='text-left-important weekly-customer-cell'>
                    <div className="weekly-customer-text">
                        <Link
                            to={`/user/${player.userName}`}
                            state={buildVisitedUserNavState(player, { from: 'PlayerList' })}
                            onClick={() => { logUserActivity(currentUser!.userName, `View User Basic: ${player.userName}`, userIpDataAdmin); }}
                            title={player.userName}
                        >
                            {player.userName}
                        </Link>
                        {filters?.password && (
                            <div className="d-block d-md-none pin-weekly" title={player.pin}>
                                {player.pin}
                            </div>
                        )}
                    </div>
                </td>
                {filters?.password && <td className='none'>{player.pin}</td>}
                {filters?.dailyWinLoss && weekDays.map((dayDate: any, index: any) => {
                    const dayName = days[getFiguresWeekdayIndex(dayDate)];
                    const balance = dayMap?.[dayName] ?? 0;
                    const dateKey = getFiguresDateKey(dayDate);
                    const hasData =
                        (ids?.betIdsByDate[dateKey]?.length ?? 0) > 0 ||
                        (ids?.adjustmentIdsByDate[dateKey]?.length ?? 0) > 0 ||
                        (ids?.casinoIdsByDate[dateKey]?.length ?? 0) > 0 ||
                        balance !== 0;

                    const balanceClass =
                        balance > 0
                            ? 'positive-admin'
                            : balance < 0
                                ? 'negative-admin'
                                : hasData
                                    ? 'zero-admin'
                                    : 'normal-color';

                    const displayValue = balance % 1 !== 0 ? formatCurrency(balance) : formatNumbers(balance);

                    return (
                        isVisible(days1[index]) ? (
                        <td
                            key={index}
                            className={`col-${days1[index]} weekly-metric-cell ${balanceClass} letter-right`}
                            style={{ cursor: hasData ? "pointer" : "default" }}
                            onClick={() => {
                                if (!hasData) return;
                                void onDayClick(player.id, dayDate);
                            }}
                        >
                            {displayValue}
                        </td>
                        ) : null
                    );
                })}
                {isVisible('week') && (
                <td
                    className={`${playerWeeklyTotal < 0
                        ? 'negative-admin'
                        : playerWeeklyTotal > 0
                            ? 'positive-admin'
                            : hasWeekData
                                ? 'zero-admin'
                                : ''
                        } col-week weekly-metric-cell letter-right`}
                    onClick={() => {
                        if (!hasWeekData) return;
                        void onWeekClick(player.id, weekDays[0]);
                    }}
                    style={{ cursor: hasWeekData ? "pointer" : "default" }}
                >
                    {formatNumbers(playerWeeklyTotal)}
                </td>
                )}
                {filters?.pendingBalance && isVisible('pending') && (
                    <td
                        className={`col-pending weekly-metric-cell letter-right`}
                        style={{ cursor: hasPending ? "pointer" : "default" }}
                        onClick={() => {
                            if (!hasPending) return;
                            void onPendingClick(player.id);
                        }}
                    >
                        {formatNumbers(pendingAmount)}
                    </td>
                )}

                {filters?.depositsWithdrawals && isVisible('depositsWithdrawal') && (
                    <td
                        className={`col-depositsWithdrawal weekly-metric-cell letter-right ${depositsTotal < 0
                            ? 'negative-admin'
                            : depositsTotal > 0
                                ? 'positive-admin'
                                : hasDepositsWithdrawals
                                    ? 'zero-admin'
                                    : ''
                            }`}
                        style={{ cursor: hasDepositsWithdrawals ? "pointer" : "default" }}
                        onClick={() => {
                            if (!hasDepositsWithdrawals) return;
                            void onDepositsWithdrawalsClick(player.id);
                        }}
                    >
                        {formatNumbers(depositsTotal)}
                    </td>
                )}
                {filters?.endOfWeekBalance &&
                    <td className={`weekly-balance-cell ${player.balance > 0 ? 'positive-admin' : player.balance < 0 ? 'negative-admin' : ''} letter-right`}>
                        {formatNumbers(player.balance)}
                    </td>}
                {filters?.lastLogin && <td className='none'>{player.lastLogin ? formatDay(player.lastLogin.toDate()) : 'N/A'}</td>}
                {filters?.lastWager && <td className='none'></td>}
            </tr>
        );
    };

    const renderAgentSummaryRow = (agentName: any, players: any, totalPending: any) => {
        const totalWeekly = players.reduce((sum: any, player: any) => {
            return sum + (dailyTotals[player.id]?.weeklyTotal || 0);
        }, 0);

        const totalDepositsWithdrawals = players.reduce((sum: any, player: any) => {
            return sum + (weeklyDepositsWithdrawalsByPlayer[player.id] || 0);
        }, 0);

        return (
            <tr key={agentName}>
                <td className='text-left-important weekly-customer-cell'>
                    <span>{players.length} Players</span>
                </td>
                {filters?.password && <td className='none'></td>}
                {filters?.dailyWinLoss && weekDays.map((dayDate: any, index: any) => {
                    const dayName = days[getFiguresWeekdayIndex(dayDate)];
                    const dayShort = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'][index];
                    if (!isVisible(dayShort)) return null;

                    const total = players.reduce((sum: any, player: any) => {
                        return sum + (dailyTotals[player.id]?.[dayName] || 0);
                    }, 0);

                    return (
                        <td
                            key={index}
                            className={`col-${dayShort} weekly-metric-cell ${total < 0 ? 'negative-admin' :
                                total > 0 ? 'positive-admin' :
                                    'normal-color'
                                } letter-right`}
                        >
                            {formatNumbers(total)}
                        </td>
                    );
                })}
                {isVisible('week') && (
                <td className={`${totalWeekly < 0 ? 'negative-admin' : totalWeekly > 0 ? 'positive-admin' : ''} col-week weekly-metric-cell letter-right`}>
                    {formatNumbers(totalWeekly)}
                </td>
                )}
                {filters?.pendingBalance && isVisible('pending') && (
                    <td className={`col-pending weekly-metric-cell letter-right`}>
                        {formatNumbers(totalPending)}
                    </td>
                )}
                {filters?.depositsWithdrawals && isVisible('depositsWithdrawal') && (
                    <td
                        className={`col-depositsWithdrawal weekly-metric-cell letter-right ${totalDepositsWithdrawals < 0
                            ? 'negative-admin'
                            : totalDepositsWithdrawals > 0
                                ? 'positive-admin'
                                : ''
                            }`}
                    >
                        {formatNumbers(totalDepositsWithdrawals)}
                    </td>
                )}

                {filters?.endOfWeekBalance && (
                    <td className={`weekly-balance-cell ${players.reduce((sum: any, player: any) => sum + player.balance, 0) > 0
                        ? 'positive-admin'
                        : players.reduce((sum: any, player: any) => sum + player.balance, 0) < 0
                            ? 'negative-admin'
                            : ''
                        } letter-right`}>
                        {formatNumbers(players.reduce((sum: any, player: any) => sum + player.balance, 0))}
                    </td>
                )}

                {filters?.lastLogin && <td className='none'></td>}
                {filters?.lastWager && <td className='none'></td>}
            </tr>
        );
    };

    const renderSubAgentTable = (subAgent: any) => {
        const subAgentPlayers = filterPlayersByAgent(subAgent.userName);
        const totalPendingsForSubAgent = subAgentPlayers.reduce((total: any, player: any) => total + (totalPendingPlayer[player.id] || 0), 0);

        if (subAgentPlayers.length > 0) {
            return (
                <div key={subAgent.id} className="weekly-table-wrap">
                    <div className="weekly-agent-bar">{subAgent.userName}</div>
                    <table className="table players-table table-custom">
                        <TableHeader weekDays={weekDays} formatDay={formatDay} filters={filters} currentColumnIndex={currentColumnIndex}
                            setCurrentColumnIndex={setCurrentColumnIndex} />
                        <tbody className="body-color">
                            {subAgentPlayers.map(renderPlayerRow)}
                            {renderAgentSummaryRow(subAgent.userName, subAgentPlayers, totalPendingsForSubAgent)}
                        </tbody>
                    </table>
                </div>
            );
        }
        return null;
    };

    return (
        <>
            <div>
                {customer.subAgents.length > 0 ? (
                    customer.subAgents.map(renderSubAgentTable)
                ) : (
                    <table className="table players-table table-custom">
                        <TableHeader weekDays={weekDays} formatDay={formatDay} filters={filters} currentColumnIndex={currentColumnIndex}
                            setCurrentColumnIndex={setCurrentColumnIndex} />
                        <tbody>
                            {filteredPlayers.map(renderPlayerRow)}
                            {renderAgentSummaryRow('Total', filteredPlayers, totalPendingsAmount)}
                        </tbody>
                    </table>
                )}
            </div>
        </>
    );
};

export default WeeklyFigures;
